import { formatCostUsd } from './formatCost';

/**
 * Client-side session cost estimate — mirrors services/sessionCostService.js.
 *
 * Pricing model (HeyGen LiveAvatar - Full mode):
 *   - 0.2 credits per 6 seconds
 *   - 30 second session floor (sessions under 30s billed as 30s)
 *   - Duration rounds up to nearest 6 seconds
 *   - $99 per 1,000 credits
 *
 * Example: 47s session → rounds up to 48s → 48/6 × 0.2 = 1.6 credits → 1.6 × (99/1000) = $0.1584
 */

const CREDITS_PER_6S = 0.2;
const SESSION_FLOOR_SECONDS = 30;
const PRICE_PER_1K_CREDITS = 99;
// No real conversation runs longer than this; past it the timestamps are not believable.
const MAX_FALLBACK_SECONDS = 2 * 60 * 60;

function sessionDurationSeconds(startedAt, endedAt) {
  if (!startedAt || !endedAt) return 0;
  const start = new Date(startedAt).getTime();
  const end = new Date(endedAt).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return 0;
  return (end - start) / 1000;
}

// Mirrors resolveEnd() in services/sessionCostService.js, including the plausibility
// clamp: interpolated message timestamps can span months, so a fallback longer than
// MAX_FALLBACK_SECONDS is junk and the 30s floor stands instead.
function resolveEnd(session) {
  if (session.endedAt) return { endedAt: session.endedAt, source: 'session' };
  const msgs = Array.isArray(session.messages) ? session.messages : [];
  const startMs = Date.parse(session.startedAt);
  for (let i = msgs.length - 1; i >= 0; i -= 1) {
    const t = msgs[i] && msgs[i].timestamp;
    const ms = Date.parse(t);
    if (!Number.isFinite(ms)) continue;
    if (!Number.isFinite(startMs) || ms <= startMs) break;
    if ((ms - startMs) / 1000 > MAX_FALLBACK_SECONDS) {
      return { endedAt: null, source: 'unreliable' };
    }
    return { endedAt: t, source: 'last-message' };
  }
  return { endedAt: null, source: 'none' };
}

export function estimateSessionCost(session) {
  const { endedAt, source } = resolveEnd(session);
  const rawSeconds = sessionDurationSeconds(session.startedAt, endedAt);

  // Apply 30s floor, then round up to nearest 6s
  const billableSeconds = Math.ceil(Math.max(rawSeconds, SESSION_FLOOR_SECONDS) / 6) * 6;
  const creditsUsed = (billableSeconds / 6) * CREDITS_PER_6S;
  const totalUsd = Math.round((creditsUsed * (PRICE_PER_1K_CREDITS / 1000)) * 10000) / 10000;

  return {
    sessionCostUsd: totalUsd,
    costBreakdown: {
      currency: 'USD',
      rawSeconds: Math.round(rawSeconds * 100) / 100,
      billableSeconds,
      creditsUsed: Math.round(creditsUsed * 10000) / 10000,
      creditsPer6s: CREDITS_PER_6S,
      pricePerThousandCredits: PRICE_PER_1K_CREDITS,
      totalUsd,
      endSource: source,
    },
  };
}

/**
 * Always recalculates cost from duration so stale DB values are overridden.
 */
export function enrichSessionWithCost(session) {
  if (!session) return session;
  const { sessionCostUsd, costBreakdown } = estimateSessionCost(session);
  return { ...session, sessionCostUsd, costBreakdown };
}

/** Human-readable line matching dashboard SessionTable (HeyGen credits model). */
export function formatCostBreakdownDescription(costBreakdown) {
  if (!costBreakdown) return null;
  const b = costBreakdown;
  if (b.rawSeconds == null && b.billableSeconds == null) return null;
  const total = b.totalUsd != null ? b.totalUsd : b.total;
  return (
    `${b.rawSeconds}s raw → ${b.billableSeconds}s billable × ${b.creditsPer6s} credits/6s` +
    ` = ${b.creditsUsed} credits @ $${b.pricePerThousandCredits}/1k` +
    ` = ${formatCostUsd(total)}`
  );
}